import React, { useState, useEffect } from "react";
import { useSnackBar } from "../../../hooks/useSnackBar";
import BoltTable from "../../../Components/BoltTable/BoltTable";
import { getQuoteLogs } from "../../../services/admin.services";
import { fetchShippingCompanies } from "../../../services/AdminServices/Companies/companies.services";
import { convertTime, fixedDecimals } from "../../../helpers/helpers";
import ModalWrapper from "../../../Components/Modals/ModalWrapper.jsx";
import { MdInfoOutline } from "react-icons/md";
import Tooltip from "../../../Components/Tooltip/Tooltip";
import QuoteDetails from "./Components/QuoteDetails";
import "../AdminShipments/AdminShipments.css";
import { useTranslation } from "react-i18next";

const Quotes = () => {
  const { t } = useTranslation("admin");
  const [quotes, setQuotes] = useState([]);
  const [allCarriers, setAllCarriers] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const setSnack = useSnackBar();

  useEffect(() => {
    loadCarriers();
  }, []);

  const loadCarriers = async () => {
    const response = await fetchShippingCompanies(1, 1000, "");
    if (!response.error && response?.data?.result?.data) {
      setAllCarriers(response.data.result.data);
    }
  };

  const getAllQuotes = async (page, perPage, filter) => {
    try {
      const response = await getQuoteLogs(page, perPage, filter);
      if (!response.error) {
        setQuotes(response?.data?.result || []);
      } else {
        setSnack((prev) => ({
          ...prev,
          open: true,
          text: t("quotes.somethingWentWrongWhileFetching"),
          severity: "error",
        }));
      }
    } catch (error) {
      setSnack((prev) => ({
        ...prev,
        open: true,
        text: t("quotes.errorFetchingData"),
        severity: "error",
      }));
    }
  };

  const parseAddress = (addressData) => {
    if (!addressData) return "-";

    const addr = addressData.address || addressData;

    if (typeof addr === "string") return String(addr);
    if (addr.fullAddress) return addr.fullAddress;

    if (typeof addr === "object" && addr !== null) {
      const parts = [
        addr.city,
        addr.state_code || addr.state,
        addr.zip,
        addr.country_code || addr.country,
      ].filter((val) => val !== null && val !== undefined);

      return parts.length > 0
        ? parts.map((part) => String(part)).join(", ")
        : "-";
    }
    return "-";
  };

  const getCarrierLogo = (carrierName) => {
    if (!carrierName) return null;
    const nameLower = carrierName.toLowerCase();
    const carrier = allCarriers?.find?.(
      (c) =>
        c?.name?.toLowerCase?.().includes(nameLower) ||
        nameLower.includes(c?.name?.toLowerCase?.()),
    );
    return carrier?.logo_url || null;
  };

  const renderCarrierInfo = (carrierName, amount, isSelected = false) => {
    let displayAmount = amount;
    if (typeof amount === "object" && amount !== null) {
      displayAmount = amount.total ?? amount.amount ?? amount.sub_total ?? "0";
    }

    const formattedAmount = fixedDecimals(displayAmount, 2);

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: isSelected ? "#EEF2FF" : "var(--color-ui-surface-sunken)",
          padding: "4px 10px",
          borderRadius: "6px",
          border: isSelected ? "1px solid #6366F1" : "1px solid var(--color-ui-border)",
          boxShadow: isSelected ? "0 1px 2px 0 rgba(0, 0, 0, 0.05)" : "none",
        }}
      >
        {getCarrierLogo(carrierName) ? (
          <img
            src={getCarrierLogo(carrierName)}
            alt={carrierName}
            style={{ height: "14px", objectFit: "contain" }}
          />
        ) : (
          <span
            style={{
              fontSize: "10px",
              fontWeight: "700",
              textTransform: "uppercase",
              color: isSelected ? "#4338CA" : "#4B5563",
            }}
          >
            {carrierName}
          </span>
        )}
        <span
          style={{
            fontSize: "11px",
            fontWeight: "600",
            color: isSelected ? "#312E81" : "var(--color-ui-text)",
          }}
        >
          ${formattedAmount}
        </span>
      </div>
    );
  };

  const parseQuotesColumn = (quotesData) => {
    if (!quotesData) return "-";

    let items = [];
    if (Array.isArray(quotesData)) {
      items = quotesData.filter(Boolean);
    } else if (typeof quotesData === "object" && quotesData !== null) {
      items = Object.entries(quotesData).map(([carrier, val]) => {
        let amount = val;
        let selected = false;
        if (typeof val === "object" && val !== null) {
          amount = val.total ?? val.amount ?? val.sub_total ?? 0;
          selected = val.selected ?? false;
        }
        return { carrier, amount, selected };
      });
    }

    if (items.length === 0) return "-";

    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          overflowX: "auto",
          maxWidth: "100%",
          padding: "2px 0",
        }}
        className="thin__scrollbar"
      >
        {items.slice(0, 3).map((q, i) => (
          <React.Fragment key={i}>
            {renderCarrierInfo(q.carrier, q.amount, q.selected)}
          </React.Fragment>
        ))}
        {items.length > 3 && (
          <span
            style={{ fontSize: "10px", color: "#6B7280", fontWeight: "500" }}
          >
            {t("quotes.moreCount", { count: items.length - 3 })}
          </span>
        )}
      </div>
    );
  };

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "60px",
      checked: true,
    },
    {
      id: 1,
      label: t("quotes.carrierHighlights"),
      render: (row) => parseQuotesColumn(row?.quotes),
      width: "350px",
      checked: true,
    },
    {
      id: 2,
      label: t("quotes.fromCountry"),
      render: (row) => <>{row?.description?.from_address?.country ?? "-"}</>,
      width: "140px",
      checked: true,
    },
    {
      id: 3,
      label: t("quotes.fromCity"),
      render: (row) => <>{row?.description?.from_address?.city ?? "-"}</>,
      width: "140px",
      checked: true,
    },
    {
      id: 4,
      label: t("quotes.toCountry"),
      render: (row) => <>{row?.description?.to_address?.country ?? "-"}</>,
      width: "140px",
      checked: true,
    },
    {
      id: 5,
      label: t("quotes.toCity"),
      render: (row) => <>{row?.description?.to_address?.city ?? "-"}</>,
      width: "140px",
      checked: true,
    },
    {
      id: 6,
      label: t("quotes.weight"),
      render: (row) => {
        const unitSystem = row?.description?.other?.unit?.toLowerCase();
        const weightUnit = unitSystem === "metric" ? "Kg" : "Lb";
        return (
          <span style={{ whiteSpace: "nowrap" }}>
            {row?.description?.other?.total_weight ?? "-"} {weightUnit}
          </span>
        );
      },
      width: "100px",
      checked: true,
    },
    {
      id: 7,
      label: t("quotes.dimensions"),
      render: (row) => {
        const dims = row?.description?.dimensions || [];
        const unitSystem = row?.description?.other?.unit?.toLowerCase();
        const sizeUnit = unitSystem === "metric" ? "cm" : "in";
        if (dims.length === 0) return "-";
        return (
          <div style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
            {dims.map((d, i) => (
              <div key={i}>
                L: {d.length} W: {d.width} H: {d.height} {sizeUnit}
              </div>
            ))}
          </div>
        );
      },
      width: "120px",
      checked: true,
    },
    {
      id: 8,
      label: t("quotes.createdAt"),
      render: (row) => <>{convertTime(row?.created_at)}</>,
      width: "180px",
      checked: true,
    },
    {
      id: 9,
      label: t("quotes.action"),
      render: (row) => (
        <div className="table_action__wrap">
          <Tooltip title={t("quotes.viewDetails")}>
            <div
              className="table_action__btn"
              onClick={() => setSelectedQuote(row)}
            >
              <MdInfoOutline size={10} />
              {t("quotes.details")}
            </div>
          </Tooltip>
        </div>
      ),
      width: "120px",
      fixed: "right",
      checked: true,
    },
  ];

  return (
    <div className="dispatcher__screen" style={{ maxWidth: "100%" , padding: "5px 5px"}}>
      <div className="custom__table_size1">
        <BoltTable
          tableTitle={t("quotes.quoteLogs")}
          tableId="adminQuotesTable_v2"
          message={t("quotes.noQuoteLogsAvailableTo")}
          searchPlaceHolder={t("quotes.searchQuotes")}
          getAllData={getAllQuotes}
          data={quotes}
          columns={columns}
        />
      </div>

      <ModalWrapper
        isOpen={!!selectedQuote}
        onClose={() => setSelectedQuote(null)}
        boxClassName="!w-full !max-w-4xl max-h-[95vh] overflow-hidden p-0 m-2 !border-none"
      >
        <QuoteDetails
          selectedQuote={selectedQuote}
          carriers={allCarriers}
          onClose={() => setSelectedQuote(null)}
        />
      </ModalWrapper>
    </div>
  );
};

export default Quotes;
